import { MathLib, ORACLE_PRICE_SCALE } from '@morpho-org/blue-sdk'
import { LTV_WARNING_THRESHOLD } from '@morpho-org/dapp-core'
import { DEFAULT_LLTV_BUFFER } from '@morpho-org/morpho-sdk'

/**
 * The maximum LTV morpho-sdk treats as safe for health-sensitive actions
 * (borrow, withdrawCollateral, supplyCollateralBorrow, repayWithdrawCollateral).
 *
 * morpho-sdk validates these against `effectiveLltv = LLTV − DEFAULT_LLTV_BUFFER`
 * (see `validatePositionHealth` / `validatePositionHealthAfterWithdraw` in
 * `@morpho-org/morpho-sdk`); anything above it reverts the build with
 * `BorrowExceedsSafeLtvError` / `WithdrawMakesPositionUnhealthyError`.
 *
 * `DEFAULT_LLTV_BUFFER` is imported straight from the SDK (never hardcoded as
 * 0.5%) so this bound tracks the SDK by construction — if the SDK ever changes
 * the buffer, this moves with it.
 */
export const getSdkSafeLltv = (lltv: bigint): bigint =>
  lltv > DEFAULT_LLTV_BUFFER ? lltv - DEFAULT_LLTV_BUFFER : 0n

/**
 * The LTV the UI is allowed to drive a position to — the tighter of the
 * AutoDeleverage protection cap and the morpho-sdk safe-LTV bound (invariant S2).
 *
 * The protection cap `wMulDown(protectedLltv, LTV_WARNING_THRESHOLD)` is the
 * per-position AutoDeleverage / pre-liquidation cap. On its own it can sit
 * ABOVE the SDK's `LLTV − buffer` bound (e.g. on low-LLTV markets, or if the
 * warning threshold is ever raised toward 1) — which is the "max shown vs SDK
 * revert" bug: the UI offers a max the SDK then rejects at build time.
 *
 * Taking `min(protectionCap, sdkSafeLltv)` guarantees `displayed max ≤ SDK
 * safe-LTV` for every market while preserving the AutoDeleverage cap.
 *
 * @param params.lltv - The market's liquidation LTV (WAD).
 * @param params.protectedLltv - The per-position cap from `useProtectedLltv`
 *   (equals `lltv` when AutoDeleverage is inactive, lower when active).
 * @param params.warningThreshold - The multiplicative warning threshold;
 *   defaults to the shared `LTV_WARNING_THRESHOLD`. Overridable for testing.
 */
export const getDisplayedMaxLtv = ({
  lltv,
  protectedLltv,
  warningThreshold = LTV_WARNING_THRESHOLD
}: {
  lltv: bigint
  protectedLltv: bigint
  warningThreshold?: bigint
}): bigint => {
  const protectionCap = MathLib.wMulDown(protectedLltv, warningThreshold)
  return MathLib.min(protectionCap, getSdkSafeLltv(lltv))
}

/**
 * The hard LTV ceiling for MANUAL entry — above the {@link getDisplayedMaxLtv} MAX-button
 * default, behind the review-dialog acknowledgment. Borrowing / withdrawing past it is blocked.
 *
 * Caps on `protectedLltv` — the per-position effective threshold: the AutoDeleverage `preLltv`
 * when pre-liquidation is authorized, else the market `lltv` (see `useProtectedLltv`). This keeps
 * an authorized position from being pushed into its own pre-liquidation band, mirroring how
 * {@link getDisplayedMaxLtv} already caps the MAX button on `protectedLltv`.
 *
 * INTERIM: `getSdkSafeLltv(protectedLltv)` (`− DEFAULT_LLTV_BUFFER`) because the installed
 * morpho-sdk reverts above `lltv − buffer`. Once morpho-org/sdks#884 is released and pinned, drop
 * the SDK buffer but KEEP the `protectedLltv` cap — do NOT return plain `lltv` (reopens the
 * pre-liquidation hole for AutoDeleverage positions).
 */
export const getBorrowCeilingLtv = (protectedLltv: bigint): bigint => getSdkSafeLltv(protectedLltv)

/**
 * Collateral value expressed in loan-asset terms, scaled by the oracle price —
 * exactly as morpho-sdk computes it in `validatePositionHealth`.
 */
export const getCollateralValue = (collateral: bigint, price: bigint): bigint =>
  MathLib.mulDivDown(collateral, price, ORACLE_PRICE_SCALE)
